import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  inject,
  Input,
} from '@angular/core';
import { ThemeService } from '@freelancer/theme';
import {
  CarouselArrowPosition,
  CarouselScrollBehaviour,
} from '@freelancer/ui/carousel';
import { ContainerSize } from '@freelancer/ui/container';
import type { PictureImage } from '@freelancer/ui/picture';
import { PictureLoadMethod, PictureObjectFit } from '@freelancer/ui/picture';
import { FontColor, TextSize } from '@freelancer/ui/text';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import {
  SectionCompanyLogo,
  SectionCompanyLogoColor,
} from './section-companies.types';

@Component({
  selector: 'fl-section-companies-title',
  template: ` <ng-content></ng-content> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionCompaniesTitleComponent {}

@UntilDestroy({ className: 'SectionCompaniesComponent' })
@Component({
  selector: 'fl-section-companies',
  template: `
    <fl-container
      class="SectionCompanies"
      [size]="containerSize"
    >
      <fl-text
        [size]="TextSize.SMALL"
        [sizeDesktop]="TextSize.MID"
        [color]="
          color === SectionCompanyLogoColor.LIGHT
            ? FontColor.LIGHT
            : FontColor.FOREGROUND
        "
        [flMarginBottom]="'small'"
        [flMarginBottomTablet]="'none'"
        [flMarginRightTablet]="'mid'"
        [flMarginRightDesktop]="'xlarge'"
        [flMarginRightDesktopLarge]="'xxxxlarge'"
      >
        <ng-container *ngIf="sectionCompaniesTitle; else defaultTitle">
          <ng-content select="fl-section-companies-title"></ng-content>
        </ng-container>
        <ng-template #defaultTitle>
          <div i18n="As used by text">As used by</div>
        </ng-template>
      </fl-text>
      <div class="SectionCompanies-logosContainer">
        <div
          *ngFor="let logo of logoDetails; trackBy: trackBySrc"
          class="SectionCompanies-logo"
          [flHideMobile]="true"
        >
          <fl-picture
            class="SectionCompanies-logo-image"
            [alt]="logo.alt"
            [boundedWidth]="true"
            [boundedHeight]="true"
            [loadMethod]="PictureLoadMethod.LAZY"
            [objectFit]="PictureObjectFit.SCALE_DOWN"
            [src]="logo.src"
          ></fl-picture>
        </div>

        <fl-carousel
          [currentIndex]="0"
          [slidesToShow]="3"
          [padding]="4"
          [arrowPosition]="CarouselArrowPosition.OUTSIDE"
          [scrollBehaviour]="CarouselScrollBehaviour.GROUP"
          [flShowMobile]="true"
        >
          <fl-carousel-prev-btn>
            <fl-icon [name]="'ui-arrow-left-alt'"></fl-icon>
          </fl-carousel-prev-btn>
          <fl-carousel-item
            *ngFor="let logo of logoDetails; trackBy: trackBySrc"
            class="SectionCompanies-logo"
          >
            <fl-picture
              class="SectionCompanies-logo-image"
              [alt]="logo.alt"
              [boundedWidth]="true"
              [boundedHeight]="true"
              [loadMethod]="PictureLoadMethod.LAZY"
              [objectFit]="PictureObjectFit.SCALE_DOWN"
              [src]="logo.src"
            ></fl-picture>
          </fl-carousel-item>
          <fl-carousel-next-btn>
            <fl-icon [name]="'ui-arrow-right-alt'"></fl-icon>
          </fl-carousel-next-btn>
        </fl-carousel>
      </div>
    </fl-container>
  `,
  styleUrls: ['./section-companies.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionCompaniesComponent implements OnInit {
  private theme = inject(ThemeService);
  private cdr = inject(ChangeDetectorRef);

  ContainerSize = ContainerSize;
  CarouselArrowPosition = CarouselArrowPosition;
  CarouselScrollBehaviour = CarouselScrollBehaviour;
  FontColor = FontColor;
  PictureObjectFit = PictureObjectFit;
  PictureLoadMethod = PictureLoadMethod;
  SectionCompanyLogoColor = SectionCompanyLogoColor;
  TextSize = TextSize;

  @Input() logos: readonly SectionCompanyLogo[];
  @Input() color: SectionCompanyLogoColor = SectionCompanyLogoColor.DARK;
  @Input() containerSize: ContainerSize = ContainerSize.DESKTOP_XXLARGE;

  @ContentChild(SectionCompaniesTitleComponent)
  sectionCompaniesTitle: SectionCompaniesTitleComponent;

  logoDetails: readonly PictureImage[];
  acceptedPNGLogos = [
    SectionCompanyLogo.SYDNEYMETRO,
    SectionCompanyLogo.BARRICK,
    SectionCompanyLogo.HAPAG_LLOYD,
    SectionCompanyLogo.PYBAR_MINING,
  ];
  lightOnly: SectionCompanyLogo[] = [
    SectionCompanyLogo.AUTOMATION_ANYWHERE,
    SectionCompanyLogo.UNIVERSITAT_BONN,
    SectionCompanyLogo.YARA,
  ];

  textColor: FontColor;

  async ngOnInit(): Promise<void> {
    const theme = await firstValueFrom(
      this.theme.getTheme().pipe(untilDestroyed(this)),
    );

    // Logo color should be the opposite of the current background.
    const logoColor =
      this.color === SectionCompanyLogoColor.DARK
        ? theme === 'dark'
          ? 'light'
          : 'dark'
        : this.color === SectionCompanyLogoColor.LIGHT
          ? 'light'
          : 'dark';

    // Decide which logo to use based on the logo color.
    // Some logos are pngs, some are svg and some only support light background.
    this.logoDetails = this.logos.map(logo => {
      let ext: 'png' | 'svg';
      let lColor: 'light' | 'dark';
      if (logo === SectionCompanyLogo.NASA && logoColor === 'light') {
        ext = 'png';
        lColor = 'light';
      } else if (this.lightOnly.includes(logo)) {
        ext = 'svg';
        lColor = 'dark';
      } else if (this.acceptedPNGLogos.includes(logo)) {
        ext = 'png';
        lColor = logoColor;
      } else {
        ext = 'svg';
        lColor = logoColor;
      }

      return {
        src: `company-logos/${logo}-${lColor}.${ext}`,
        alt: `${logo} logo`,
      };
    });

    this.cdr.markForCheck();
  }

  trackBySrc(_: number, item: PictureImage): string {
    return item.src;
  }
}
